<script setup lang="ts">
import Icon from "@/components/general/Icon/Icon.vue";

interface Props {
    id: string;
    icon?: string;
    prefixIcon?: string;
    btnType?: "primary" | "secondary" | "tertiary";
    disabled?: boolean;
}

const {
    id,
    icon = "",
    prefixIcon = "",
    btnType = "primary",
    disabled = false,
} = defineProps<Props>();

defineEmits(["click"]);
</script>

<template>
    <button
        :id="id"
        :class="{
            button: true,
            'with-icon': icon || prefixIcon,
            disabled,
            [btnType]: true,
        }"
        :disabled="disabled"
        :tabindex="disabled ? -1 : undefined"
        @click="$emit('click', $event)"
    >
        <Icon v-if="prefixIcon" :name="prefixIcon" class="icon" />
        <span v-if="$slots.default" class="content">
            <slot />
        </span>
        <Icon v-if="icon" :name="icon" class="icon" />
    </button>
</template>

<style>
.button {
    border-radius: 2rem;
    padding: 0.75rem 2rem;
    line-height: 1.2;
    font-family: Roboto, Arial, sans-serif;
    font-size: 1rem;
    font-weight: bold;
    cursor: pointer;
    width: 250px;
    transition:
        background-color var(--transition-duration),
        outline-color var(--transition-duration);

    &.with-icon {
        display: inline-flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5em;
    }

    &.primary {
        color: var(--pure-white);
        border: 0;
        background: var(--color-primary);

        &:hover,
        &:focus {
            background: var(--primary-hover);
        }

        &:focus {
            outline-color: var(--primary-focus-outline);
        }

        &:active {
            background: var(--primary-pressed);
            outline: 3px solid var(--pure-white);
            outline-offset: -6px;
        }
    }

    &.secondary {
        color: var(--color-primary);
        border: solid 2px var(--color-primary);
        background: var(--pure-white);

        &:hover,
        &:focus {
            background: var(--secondary-hover);
        }

        &:focus {
            outline-color: var(--secondary-pressed);
        }

        &:active {
            background: var(--secondary-pressed-light);
        }
    }

    &.tertiary {
        border: 0;
        background: initial;
        text-decoration: underline;
        font-weight: 400;

        &:focus {
            outline-color: var(--primary-hover);
        }
    }

    &:disabled {
        cursor: auto;
        background: var(--grey-warm);
        color: var(--grey-dark);

        &:hover,
        &:active,
        &:focus {
            background: var(--grey-warm);
            outline: none;
        }
    }
}

@media screen and (width <= 584px) {
    .button {
        width: 100%;
    }
}
</style>
